const ATTRIBUTE_VALUE_TYPES = [
  'S',
  'N',
  'B',
  'BOOL',
  'NULL',
  'M',
  'L',
  'SS',
  'NS',
  'BS',
] as const

type DynamoDBAttributeValueType = (typeof ATTRIBUTE_VALUE_TYPES)[number]

type ChangeLogValueEnvelope = {
  version?: number
  kind?: string
  type?: string
  value?: unknown
}

export type ChangeLogProperty = {
  key: string
  values: unknown[]
  type?: string
}

export const isSystemChangeLogActor = (
  changedBy: string | null | undefined,
): changedBy is string => changedBy?.startsWith('system:') ?? false

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value)

const getAttributeValueType = (
  value: Record<string, unknown>,
): DynamoDBAttributeValueType | undefined =>
  ATTRIBUTE_VALUE_TYPES.find((type) =>
    Object.prototype.hasOwnProperty.call(value, type),
  )

const isAttributeValueType = (
  value: string | undefined,
): value is DynamoDBAttributeValueType =>
  ATTRIBUTE_VALUE_TYPES.includes(value as DynamoDBAttributeValueType)

const parseDynamoDBNumber = (value: unknown): number | string | unknown => {
  if (typeof value !== 'string') return value

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : value
}

export const isDynamoDBAttributeValue = (value: unknown) =>
  isRecord(value) && Boolean(getAttributeValueType(value))

export const decodeDynamoDBAttributeValue = (value: unknown): unknown => {
  if (!isRecord(value)) return value

  const type = getAttributeValueType(value)

  switch (type) {
    case 'S':
      return value.S
    case 'N':
      return parseDynamoDBNumber(value.N)
    case 'B':
      return value.B
    case 'BOOL':
      return value.BOOL
    case 'NULL':
      return null
    case 'M': {
      const mapValue = isRecord(value.M) ? value.M : {}
      return Object.entries(mapValue).reduce<Record<string, unknown>>(
        (acc, [key, item]) => {
          acc[key] = decodeDynamoDBAttributeValue(item)
          return acc
        },
        {},
      )
    }
    case 'L':
      return Array.isArray(value.L)
        ? value.L.map(decodeDynamoDBAttributeValue)
        : []
    case 'SS':
      return Array.isArray(value.SS) ? value.SS : []
    case 'NS':
      return Array.isArray(value.NS) ? value.NS.map(parseDynamoDBNumber) : []
    case 'BS':
      return Array.isArray(value.BS) ? value.BS : []
    default:
      return value
  }
}

const isVersionedChangeLogValue = (
  value: unknown,
): value is ChangeLogValueEnvelope =>
  isRecord(value) &&
  value.version === 1 &&
  value.kind === 'ddb' &&
  Object.prototype.hasOwnProperty.call(value, 'value')

export const decodeChangeLogValue = (input: unknown): unknown => {
  if (typeof input === 'string') {
    try {
      const parsed = JSON.parse(input) as unknown
      return typeof parsed === 'string' ? parsed : decodeChangeLogValue(parsed)
    } catch {
      return input
    }
  }

  if (isVersionedChangeLogValue(input)) {
    if (input.type === 'NOT_EXIST') return 'NOT_EXIST'

    if (isAttributeValueType(input.type)) {
      return decodeDynamoDBAttributeValue({ [input.type]: input.value })
    }

    return decodeChangeLogValue(input.value)
  }

  if (isDynamoDBAttributeValue(input)) {
    return decodeDynamoDBAttributeValue(input)
  }

  return input
}

export const stringifyDecodedChangeLogValue = (value: unknown): string => {
  if (value == null) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }

  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

export const stringifyChangeLogValue = (input: unknown): string =>
  stringifyDecodedChangeLogValue(decodeChangeLogValue(input))

const normalizeProperty = (value: unknown): ChangeLogProperty | null => {
  if (!isRecord(value)) return null

  const key = typeof value.key === 'string' ? value.key : ''
  const type = typeof value.type === 'string' ? value.type : ''
  const values = Array.isArray(value.values)
    ? value.values
    : value.values === undefined || value.values === null
      ? []
      : [value.values]

  return { key, type, values }
}

const extractLegacyPropertyBlocks = (str: string): string[] => {
  const blocks: string[] = []
  let start = -1
  let depth = 0

  for (let index = 0; index < str.length; index += 1) {
    const char = str[index]

    if (char === '{') {
      if (depth === 0) start = index
      depth += 1
      continue
    }

    if (char !== '}' || depth === 0) continue

    depth -= 1

    if (depth === 0 && start >= 0) {
      blocks.push(str.slice(start, index + 1))
      start = -1
    }
  }

  return blocks
}

const parseLegacyPropertyString = (str?: string): ChangeLogProperty[] => {
  if (!str) return []

  const cleanStr = str.replace(/^['"]|['"]$/g, '')
  const propertyStrings = extractLegacyPropertyBlocks(cleanStr)

  return propertyStrings.map((propStr: string) => {
    const valuesMatch = /values=\[([^\]]+)\]/.exec(propStr)
    let values: unknown[] = []
    if (valuesMatch) {
      const valueStr = valuesMatch[1]
      if (valueStr.includes('=')) {
        values = [valueStr]
      } else {
        values = valueStr.split(',').map((v) => v.trim())
      }
    }

    const typeMatch = /type=([^,}]+)/.exec(propStr)
    const keyMatch = /key=([^,}]+)/.exec(propStr)

    return {
      values,
      type: typeMatch ? typeMatch[1] : '',
      key: keyMatch ? keyMatch[1] : '',
    }
  })
}

export const parseChangeLogProperties = (
  input?: unknown,
): ChangeLogProperty[] => {
  const value = decodeChangeLogValue(input)

  if (Array.isArray(value)) {
    return value.map(normalizeProperty).filter(Boolean) as ChangeLogProperty[]
  }

  if (isRecord(value) && 'key' in value && 'values' in value) {
    const property = normalizeProperty(value)
    return property ? [property] : []
  }

  return typeof value === 'string' ? parseLegacyPropertyString(value) : []
}
